"use client";

import { useTranslations } from "next-intl";
import { SectionLayout } from "@/components/layout/SectionLayout";
import { Title } from "@/components/common/typography/Title";
import { Icon } from "@iconify/react";
import logo from "@/assets/images/logos/logo.svg";
import Image from "next/image";
import { motion } from "framer-motion";
import {
  comparisonData,
  criteria,
  type ComparisonItem,
  type Criterion,
} from "../../data/comparison-data";

export const WhyChooseUsSection = () => {
  const t = useTranslations("whyChooseUs");

  // Mapper les données avec les traductions
  const translatedComparisonData = comparisonData.map((item) => ({
    title: t(item.titleKey),
    cost: { ...item.cost, text: t(item.cost.textKey) },
    quality: { ...item.quality, text: t(item.quality.textKey) },
    deadline: { ...item.deadline, text: t(item.deadline.textKey) },
    flexibility: { ...item.flexibility, text: t(item.flexibility.textKey) },
    highlight: item.highlight,
  }));

  // Mapper les critères avec les traductions
  const translatedCriteria = criteria.map((criterion) => ({
    key: criterion.key,
    label: t(criterion.labelKey),
  }));

  // Réorganiser les données pour mobile : mettre Virtuosa en dernier
  const mobileComparisonData = [
    ...translatedComparisonData.filter((option) => !option.highlight),
    ...translatedComparisonData.filter((option) => option.highlight),
  ];

  // Variants pour les animations
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 20,
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut",
      },
    },
  };

  const tableRowVariants = {
    hidden: {
      opacity: 0,
      x: -20,
    },
    visible: {
      opacity: 1,
      x: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut",
      },
    },
  };

  const virtuosaColumnVariants = {
    hidden: {
      opacity: 0,
      scale: 0.95,
    },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        duration: 0.7,
        ease: "easeOut",
        delay: 0.3,
      },
    },
  };

  return (
    <SectionLayout>
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: "easeOut" }}
        viewport={{ once: true, margin: "-50px" }}
        className="w-full"
      >
        <Title title={t("section.title")} subtitle={t("section.subtitle")} />
      </motion.div>

      {/* Section de comparaison */}
      <div className="w-full">
        {/* Version Desktop - Tableau */}
        <div className="hidden md:block">
          <motion.div
            initial={{ opacity: 0, scale: 0.98 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            viewport={{ once: true, margin: "-100px" }}
            className="rounded-lg bg-tertiary p-2"
          >
            <div className="rounded-lg bg-primary p-6 shadow-sm">
              {/* En-têtes du tableau */}
              <motion.div
                variants={containerVariants}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-100px" }}
                className="mb-6 grid grid-cols-4 gap-6"
              >
                <motion.div
                  variants={itemVariants}
                  className="text-lg font-semibold text-secondary"
                ></motion.div>
                {translatedComparisonData.map((option, index) => (
                  <motion.div
                    key={index}
                    variants={
                      option.highlight ? virtuosaColumnVariants : itemVariants
                    }
                    className="text-left text-lg font-medium text-secondary"
                  >
                    {option.highlight ? (
                      <Image
                        src={logo}
                        alt="Virtuosa - Agence Web Premium | Comparaison concurrentielle"
                        className="w-22 h-auto"
                        width={98}
                        height={98}
                        priority={true}
                      />
                    ) : (
                      option.title
                    )}
                  </motion.div>
                ))}
              </motion.div>

              {/* Lignes du tableau */}
              <motion.div
                variants={containerVariants}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-100px" }}
              >
                {translatedCriteria.map((criterion, criterionIndex) => (
                  <motion.div
                    key={criterion.key}
                    variants={tableRowVariants}
                    className={`grid grid-cols-4 gap-6 py-4 ${
                      criterionIndex !== translatedCriteria.length - 1
                        ? "border-b border-accent/20"
                        : ""
                    }`}
                  >
                    {/* Critère */}
                    <div className="font-medium text-secondary">
                      {criterion.label}
                    </div>

                    {/* Valeurs pour chaque option */}
                    {translatedComparisonData.map((option, optionIndex) => {
                      const value = option[criterion.key];
                      return (
                        <div
                          key={optionIndex}
                          className="flex items-start justify-start gap-3"
                        >
                          <Icon
                            icon={value.icon}
                            className="mt-1 h-5 w-5 flex-shrink-0 text-accent"
                          />
                          <span className="text-secondary">{value.text}</span>
                        </div>
                      );
                    })}
                  </motion.div>
                ))}
              </motion.div>
            </div>
          </motion.div>
        </div>

        {/* Version Mobile - Cartes empilées */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-50px" }}
          className="space-y-6 md:hidden"
        >
          {mobileComparisonData.map((option, index) => (
            <motion.div
              key={index}
              variants={
                option.highlight ? virtuosaColumnVariants : itemVariants
              }
              className={`rounded-md p-6 ${
                option.highlight
                  ? "border-2 border-secondary bg-primary shadow-lg"
                  : "bg-tertiary shadow-sm"
              }`}
            >
              {/* Titre de l'option */}
              <h3
                className={`mb-4 text-left text-xl font-bold ${
                  option.highlight ? "text-secondary" : "text-secondary"
                }`}
              >
                {option.title}
              </h3>

              {/* Liste des critères */}
              <div className="space-y-4">
                {translatedCriteria.map((criterion) => {
                  const value = option[criterion.key];
                  return (
                    <motion.div
                      key={criterion.key}
                      initial={{ opacity: 0, x: -10 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      transition={{
                        duration: 0.4,
                        ease: "easeOut",
                        delay: index * 0.1,
                      }}
                      viewport={{ once: true }}
                      className="flex items-start gap-3"
                    >
                      <Icon
                        icon={value.icon}
                        className="mt-1 h-5 w-5 flex-shrink-0 text-accent"
                      />
                      <div className="flex-1">
                        <span
                          className={`font-medium ${
                            option.highlight
                              ? "text-secondary"
                              : "text-secondary"
                          }`}
                        >
                          {criterion.label} :{" "}
                        </span>
                        <span
                          className={`${
                            option.highlight
                              ? "text-secondary"
                              : "text-secondary"
                          }`}
                        >
                          {value.text}
                        </span>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </SectionLayout>
  );
};
